import { useState } from 'react';
import {
  BankAccountInfiniteSelectField,
  DocumentTypesInfiniteSelectField,
  FloatingFormItem,
  PlanAccountsSelect,
} from '@/components/_common';
import CostCenterInfiniteSelectField from '@/components/_common/CostCenterInfiniteSelectField';
import EmployeesSelectedModal from '@/components/_common/EmployeesSelectedModal';
import SuppliersSelectedModal from '@/components/_common/SuppliersSelectedModal';
import {
  PlanAccountDestination,
  PlanAccountHierarchy,
} from '@/core/domain/enterprise/PlanAccounts/PlanAccounts.types';
import { ReportBillsToPayFormValues } from '@/core/domain/reports/financial/billsToPay/BillsToPay.types';
import { useDisclosure, useWindowSize } from '@/hooks';
import {
  Badge,
  Button,
  Col,
  Collapse,
  Form,
  FormInstance,
  Input,
  Row,
  Space,
} from 'antd';
import { useIntl } from 'react-intl';

interface Props {
  form: FormInstance<ReportBillsToPayFormValues>;
}

export default function BillsToPayMoreOptions({ form }: Props) {
  const intl = useIntl().formatMessage;
  const [modalEmployeeVisible, modalEmployeeAction] = useDisclosure();
  const [selectedEmployees, setSelectedEmployees] = useState<number[]>();

  const [modalSupplierVisible, modalSupplierAction] = useDisclosure();
  const [selectedSuppliers, setSelectedSuppliers] = useState<number[]>();
  const { isMobile } = useWindowSize();

  const handleSupplierSelect = (suppliersIds?: number[]) => {
    setSelectedSuppliers(suppliersIds);
    form.setFieldValue('suppliersIds', suppliersIds);
    modalSupplierAction.close();
  };

  const handleEmployeeSelect = (employeesIds?: number[]) => {
    setSelectedEmployees(employeesIds);
    form.setFieldValue('employeesIds', employeesIds);
    modalEmployeeAction.close();
  };
  return (
    <>
      <Collapse>
        <Collapse.Panel
          key={1}
          header={intl({ id: 'financial.billsToPay.label.moreOptions' })}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={24} md={8}>
              <FloatingFormItem
                name="planAccountIds"
                label={intl({ id: 'financial.billsToPay.label.accountPlans' })}
              >
                <PlanAccountsSelect
                  queryFilter={{
                    destination: PlanAccountDestination.Expense,
                    hierarchy: PlanAccountHierarchy.Analitical,
                  }}
                  mode={'multiple'}
                  allowClear
                />
              </FloatingFormItem>
            </Col>

            <Col xs={24} sm={24} md={8}>
              <FloatingFormItem
                name="documentTypeIds"
                label={intl({ id: 'financial.billsToPay.label.documentType' })}
              >
                <DocumentTypesInfiniteSelectField
                  queryFilter={{ active: true }}
                  mode="multiple"
                  allowClear
                />
              </FloatingFormItem>
            </Col>

            <Col xs={24} sm={24} md={8}>
              <FloatingFormItem
                name="costCenterIds"
                label={intl({ id: 'financial.billsToPay.label.costCenter' })}
              >
                <CostCenterInfiniteSelectField
                  queryFilter={{ active: true }}
                  mode="multiple"
                  allowClear
                />
              </FloatingFormItem>
            </Col>
            <Col xs={24} sm={24} md={8}>
              <FloatingFormItem
                name="bankAccountIds"
                label={intl({ id: 'financial.billsToPay.label.bankAccount' })}
              >
                <BankAccountInfiniteSelectField
                  queryFilter={{ active: true }}
                  mode={'multiple'}
                  allowClear
                />
              </FloatingFormItem>
            </Col>

            <Col xs={24} sm={24} md={8}>
              <Button
                block
                type="primary"
                onClick={modalSupplierAction.open}
                size={isMobile ? 'small' : 'large'}
              >
                <Space direction={isMobile ? 'vertical' : 'horizontal'}>
                  {intl({ id: 'financial.billsToPay.button.selectSuppliers' })}
                  <Badge count={form.getFieldsValue().suppliersIds?.length} />
                </Space>
              </Button>
            </Col>

            <Col xs={24} sm={24} md={8}>
              <Button
                block
                type="primary"
                onClick={modalEmployeeAction.open}
                size={isMobile ? 'small' : 'large'}
              >
                <Space direction={isMobile ? 'vertical' : 'horizontal'}>
                  {intl({ id: 'financial.billsToPay.button.selectEmployees' })}
                  <Badge count={form.getFieldsValue().employeesIds?.length} />
                </Space>
              </Button>
            </Col>
          </Row>
        </Collapse.Panel>
      </Collapse>

      <Form.Item hidden noStyle name="employeesIds">
        <Input />
      </Form.Item>

      <Form.Item hidden noStyle name="suppliersIds">
        <Input />
      </Form.Item>

      <EmployeesSelectedModal
        visible={modalEmployeeVisible}
        handleVisible={modalEmployeeAction.toggle}
        selectedEmployeesIds={selectedEmployees}
        finishCallback={handleEmployeeSelect}
      />

      <SuppliersSelectedModal
        visible={modalSupplierVisible}
        handleVisible={modalSupplierAction.toggle}
        selectSuppliersIds={selectedSuppliers}
        finishCallback={handleSupplierSelect}
      />
    </>
  );
}
